"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getCartItemKey, useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/currency";
import { handleMediaError, resolveMediaUrl } from "../utils/media";
import { getProductPath } from "../utils/product";
import { ShoppingBag, Trash2, ArrowLeft, CreditCard } from "lucide-react";
import toast from "react-hot-toast";

export default function CartPage() {
  const { cartItems, updateQuantity, removeFromCart, subtotal, itemCount } = useCart();
  const router = useRouter();

  if (cartItems.length === 0) {
    return (
      <div className="flex-1 flex flex-col justify-center items-center py-20 bg-zinc-50 dark:bg-black text-center px-4">
        <div className="h-16 w-16 rounded-full bg-violet-100 dark:bg-violet-950/50 flex items-center justify-center text-violet-600 dark:text-violet-400 mb-6">
          <ShoppingBag className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Your Cart is Empty</h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2 max-w-xs leading-relaxed">
          Looks like you haven&apos;t added any premium gadgets to your cart yet. Browse our shop to get started.
        </p>
        <Link
          href="/shop"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3 text-sm font-semibold text-white hover:bg-violet-500 transition-colors shadow-lg shadow-violet-600/10"
        >
          Explore Shop Catalog
        </Link>
      </div>
    );
  }

  const shippingCost = 0;
  const totalCost = subtotal + shippingCost;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 w-full flex-1 bg-zinc-50 dark:bg-black transition-colors duration-300">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
        Shopping Cart ({itemCount} items)
      </h1>

      <div className="grid lg:grid-cols-12 gap-8 mt-8 items-start">
        
        {/* Left Side: Cart Items List */}
        <div className="lg:col-span-8 space-y-4">
          <div className="bg-white dark:bg-zinc-950 rounded-2xl border border-zinc-200 dark:border-zinc-900 p-4 md:p-6 space-y-4 divide-y divide-zinc-100 dark:divide-zinc-900">
            {cartItems.map((item, idx) => {
              const product = item.product;
              const itemKey = getCartItemKey(item);
              const displayImage = resolveMediaUrl(item.variant?.images?.[0] || product.primaryImage || product.images?.[0]) || "https://images.unsplash.com/photo-1546868871-7041f2a55e12?q=80&w=200&auto=format&fit=crop";
              const availableStock = item.variant?.stock ?? product.stock;
              
              return (
                <div key={itemKey || idx} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-4 first:pt-0">
                  {/* Thumbnail */}
                  <div className="h-20 w-20 rounded-xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden flex items-center justify-center shrink-0 p-1">
                    <img src={displayImage} alt="" onError={handleMediaError} className="max-h-full max-w-full object-contain" />
                  </div>

                  {/* Title and Specs */}
                  <div className="flex-1 min-w-0">
                    <Link href={getProductPath(product)} className="font-bold text-sm text-zinc-900 dark:text-white hover:text-violet-500 transition-colors line-clamp-1">
                      {product.name}
                    </Link>
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1 uppercase font-semibold">
                      {product.category}
                    </p>
                    {item.variant && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300">
                        <span className="h-3 w-3 rounded-full border border-zinc-300 dark:border-zinc-700" style={{ backgroundColor: item.variant.hexColor }} />
                        {item.variant.colorName}
                      </p>
                    )}
                    {availableStock < 5 && (
                      <span className="text-[10px] text-amber-500 font-bold mt-1 block">
                        Only {availableStock} items left in stock
                      </span>
                    )}
                  </div>

                  {/* Quantity controls */}
                  <div className="flex items-center rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 shrink-0">
                    <button
                      onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                      className="px-2.5 py-1 text-zinc-500 font-bold hover:bg-zinc-100 dark:hover:bg-zinc-850 rounded-l-lg transition-colors"
                    >
                      -
                    </button>
                    <span className="px-3 text-xs font-semibold text-zinc-950 dark:text-zinc-50">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                      className="px-2.5 py-1 text-zinc-500 font-bold hover:bg-zinc-100 dark:hover:bg-zinc-850 rounded-r-lg transition-colors"
                    >
                      +
                    </button>
                  </div>

                  {/* Total price & delete */}
                  <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto shrink-0 border-t sm:border-0 border-zinc-100 dark:border-zinc-900 pt-3 sm:pt-0 mt-2 sm:mt-0">
                    <div className="flex flex-col text-right sm:text-right">
                      <span className="text-xs text-zinc-400">Total</span>
                      <span className="font-bold text-sm text-zinc-900 dark:text-white tracking-tight">
                        {formatCurrency(product.price * item.quantity)}
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        removeFromCart(itemKey);
                        toast.success(`${product.name} removed from cart`);
                      }}
                      className="p-1.5 text-zinc-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                      title="Remove product"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <Link href="/shop" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-indigo-500 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
            Continue shopping premium products
          </Link>
        </div>

        {/* Right Side: Order Summary Panel */}
        <div className="lg:col-span-4 bg-white dark:bg-zinc-950 rounded-2xl border border-zinc-200 dark:border-zinc-900 p-6 space-y-6">
          <h2 className="text-md font-bold text-zinc-900 dark:text-white border-b border-zinc-100 dark:border-zinc-900 pb-3">
            Payment Summary
          </h2>

          <div className="space-y-3.5 text-sm">
            <div className="flex justify-between text-zinc-600 dark:text-zinc-400">
              <span>Cart Subtotal</span>
              <span className="font-medium text-zinc-900 dark:text-zinc-200">{formatCurrency(subtotal)}</span>
            </div>
            
            <div className="flex justify-between text-zinc-600 dark:text-zinc-400">
              <span>Delivery Charges</span>
              <span className="text-zinc-500">Selected at checkout</span>
            </div>

            <div className="border-t border-zinc-100 dark:border-zinc-900 pt-3 flex justify-between font-bold text-zinc-950 dark:text-white">
              <span>Order Total</span>
              <span className="text-lg tracking-tight">{formatCurrency(totalCost)}</span>
            </div>
          </div>

          <button
            onClick={() => router.push("/checkout")}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-violet-600 text-white py-3.5 text-sm font-bold hover:bg-violet-500 transition-colors shadow-lg shadow-violet-600/10 cursor-pointer"
          >
            <CreditCard className="h-4 w-4" />
            Proceed to Checkout
          </button>
        </div>
      </div>
    </div>
  );
}
